import React, { useState, useMemo, useEffect } from "react";
import "./custom-scrollbar.css";
import { ButtonPrimary } from "../common/ButtonPrimary";
import { StandardButton } from "../common/StandardButton";
import { useAppSelector } from "@/store/hooks";
import { useDiscountUpdate } from "@/hooks/useDiscountUpdate";
import {
  AmountOffProducts,
  ApplicableProductsKeyType,
  BuyXGetY,
  PageType,
  Product,
  SelectedProduct,
} from "@/types/coupon/discount";
import { getProducts, searchProducts } from "@/services/discountService";
import { toast } from "sonner";
import ProductCard from "./ProductCard";
import { X, Package } from "lucide-react";
import { generateImageSrc } from "@/utils/helper";

interface Variant {
  id: string;
  title: string;
  image: string;
  sku: string;
  amount: number;
}

interface ProductsApiResponse {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  message: string;
  valid: boolean;
  data: Product[];
}

// Helper to extract image path from assets array
const getImageFromAssets = (assets: any[]): string => {
  if (!Array.isArray(assets) || assets.length === 0) return "";
  return assets[0]?.asset?.path || "";
};

// Helper to build variant title from options
const buildVariantTitle = (option1?: string, option2?: string, option3?: string): string => {
  const options = [option1, option2, option3].filter(Boolean);
  const title = options.length > 0 ? options.join(" / ") : "";
  console.log("[buildVariantTitle]", { option1, option2, option3, title });
  return title;
};

interface ProductTableProps {
  currentPage: PageType;
  onClose: () => void;
  applicableProductsKey: ApplicableProductsKeyType;
  searchValue: string;
  prefillSelected?: SelectedProduct[];
  initialTab?: "selected" | "all";
  activeTab?: "selected" | "all";
  onTabChange?: (tab: "selected" | "all") => void;
  saveTriggerRef?: React.MutableRefObject<(() => void) | null>;
}

export default function ProductTable({
  currentPage,
  onClose,
  applicableProductsKey,
  searchValue,
  prefillSelected,
  initialTab = "all",
  activeTab: externalActiveTab,
  onTabChange,
  saveTriggerRef,
}: ProductTableProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const applicable_products = useAppSelector((state) => {
    const pageData = state.discount[currentPage];

    switch (currentPage) {
      case "amountOffProducts":
        if (applicableProductsKey === "applicable_products") {
          return (pageData as AmountOffProducts).applicable_products;
        }
        break;

      case "buyXGetY":
        if (
          applicableProductsKey === "applicable_x_products" ||
          applicableProductsKey === "applicable_y_products"
        ) {
          return (pageData as BuyXGetY)[applicableProductsKey];
        }
        break;
    }

    return [];
  }) as SelectedProduct[];

  const [selectedVariants, setSelectedVariants] = useState<SelectedProduct[]>(
    prefillSelected ?? applicable_products,
  );
  const handleUpdate = useDiscountUpdate();
  const [rawData, setRawData] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    // If prefillSelected is provided, use it to prefill selection (for edit modal)
    if (prefillSelected) {
      setSelectedVariants(prefillSelected);
    } else {
      setSelectedVariants(applicable_products);
    }
  }, [applicable_products, prefillSelected]);

  const handleVariantToggle = (product: Product, variant: Variant) => {
    setSelectedVariants((prev) => {
      const parentId = product.id;
      const parentName = product.title;
      const existingParent = prev.find((p) => p.id === parentId);
      const parentTotalVariants = product.variants?.length ?? 0;

      if (existingParent) {
        const alreadySelected = existingParent.variants.some(
          (v) => v.id === variant.id,
        );
        const updatedVariants = alreadySelected
          ? existingParent.variants.filter((v) => v.id !== variant.id)
          : [...existingParent.variants, {
              id: variant.id,
              title: variant.title,
              image: variant.image,
              sku: variant.sku,
              amount: variant.amount,
            }];

        if ((updatedVariants?.length ?? 0) === 0) {
          return prev.filter((p) => p.id !== parentId);
        }

        return prev.map((p) =>
          p.id === parentId
            ? {
                ...p,
                variants: updatedVariants,
                totalVariants: parentTotalVariants,
              }
            : p,
        );
      } else {
        return [
          ...prev,
          {
            id: parentId,
            title: parentName,
            totalVariants: parentTotalVariants,
            images: product.images || [],
            sku: product.sku || "",
            amount: product.amount || 0,
            variants: [{
              id: variant.id,
              title: variant.title,
              image: variant.image,
              sku: variant.sku,
              amount: variant.amount,
            }],
          },
        ];
      }
    });
  };

  const handleProductToggle = (product: Product) => {
    setSelectedVariants((prev) => {
      const existingParent = prev.find((p) => p.id === product.id);
      const totalVariants = product.variants?.length ?? 0;
      const selectedCount = existingParent?.variants?.length ?? 0;
      const isPartiallySelected = selectedCount > 0 && selectedCount < totalVariants;
      const isFullySelected = selectedCount === totalVariants && totalVariants > 0;

      if (isPartiallySelected || !isFullySelected) {
        // Select all variants
        setExpanded((prev) => ({ ...prev, [product.id]: true }));
        return [
          ...prev.filter((p) => p.id !== product.id),
          {
            id: product.id,
            title: product.title,
            totalVariants: totalVariants,
            images: product.images || [],
            sku: product.sku || "",
            amount: product.amount || 0,
            variants: (product.variants || []).map((v) => ({
              id: v.id,
              title: v.title,
              image: v.image,
              sku: v.sku,
              amount: v.amount,
            })),
          },
        ];
      } else {
        // Deselect all
        setExpanded((prev) => ({ ...prev, [product.id]: false }));
        return prev.filter((p) => p.id !== product.id);
      }
    });
  };

  const onSave = () => {
    handleUpdate(currentPage, {
      [applicableProductsKey]: selectedVariants as [],
    });
    toast.success("Products selected successfully");
    onClose();
  };

  // Connect saveTriggerRef to onSave function
  React.useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = onSave;
    }
  }, [saveTriggerRef, onSave]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        let res;
        if (searchValue && searchValue.trim().length > 0) {
          const queryParams = {
            page: pageIndex,
            limit: pageSize,
            q: searchValue,
          };
          console.log("[ProductSearch] Query Params:", queryParams);
          res = await searchProducts(queryParams);
          console.log("[ProductSearch] Response:", res);
        } else {
          // If query is empty (including after erase), fallback to normal product list
          const queryParams = {
            page: pageIndex,
            page_size: pageSize,
          };
          console.log("[ProductList] Query Params:", queryParams);
          res = await getProducts(queryParams);
          console.log("[ProductList] Response:", res);
        }
        
        // The response structure is: res.data = { data: [...], message: ..., ... }
        const responseData = res?.data as ProductsApiResponse | undefined;
        console.log("[ProductList] Response Data:", responseData);
        
        // Get products array from response - handle both structures
        const productsArray = Array.isArray(responseData) 
          ? responseData 
          : (responseData?.data || []);
        console.log("[ProductList] Products Array:", productsArray, "Length:", productsArray.length);
        
        // Normalize variants for all products, including search results
        const normalizedData = productsArray.map((product: any) => {
          let variants = Array.isArray(product.variants)
            ? product.variants
            : [];
          // If variants are missing but product has a variants field as object, convert to array
          if (
            !Array.isArray(product.variants) &&
            product.variants &&
            typeof product.variants === "object"
          ) {
            variants = Object.values(product.variants);
          }
          
          // Transform variants to include proper image, title, and price
          const transformedVariants = variants.map((v: any) => ({
            id: v.id,
            title: buildVariantTitle(v.option1, v.option2, v.option3),
            image: getImageFromAssets(v.assets),
            sku: v.sku || "",
            amount: v.price || 0,
          }));
          
          // Extract product images
          const productImages = Array.isArray(product.assets)
            ? product.assets.map((a: any) => a.asset?.path).filter(Boolean)
            : [];
          
          return {
            ...product,
            variants: transformedVariants,
            totalVariants: transformedVariants.length,
            images: productImages,
          };
        });
        setRawData(normalizedData);
        setTotal(responseData?.total || productsArray.length);
        setTotalPages(responseData?.total_pages || 1);
        console.log("[ProductList] Normalized data:", normalizedData);
        console.log("[ProductList] Total products:", normalizedData.length);
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setError("Failed to fetch products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [pageIndex, pageSize, searchValue]);

  const [activeTab, setActiveTab] = useState<"selected" | "all">(initialTab);

  // Use external activeTab if provided, otherwise use local state
  const currentActiveTab =
    externalActiveTab !== undefined ? externalActiveTab : activeTab;
  const handleTabChange = onTabChange || setActiveTab;

  return (
    <div className="rounded-xl flex flex-col h-135 bg-background shadow-lg border border-border">
      {/* Tabs */}
      <div className="flex gap-2 px-6 pt-3 pb-4 border-b bg-background rounded-t-xl">
        <StandardButton
          variant={currentActiveTab === "all" ? "primary" : "secondary"}
          onClick={() => handleTabChange("all")}
        >
          All Products
        </StandardButton>
        <StandardButton
          variant={currentActiveTab === "selected" ? "primary" : "secondary"}
          onClick={() => handleTabChange("selected")}
        >
          Selected Products
        </StandardButton>
      </div>
      
      {/* Tab Content */}
      <div
        className="flex-1 overflow-y-auto px-6 py-3 custom-scrollbar"
        style={{ maxHeight: 58 * 10 + 48 + 1 }}
      >
        {loading ? (
          <div className="text-center text-muted-foreground py-8">Loading products...</div>
        ) : error ? (
          <div className="text-center text-destructive py-8">{error}</div>
        ) : currentActiveTab === "all" ? (
          rawData.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No products found.
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden bg-background">
              {rawData.map((product) => {
                const selectedProduct = selectedVariants.find((p) => p.id === product.id);
                const selectedVariantsCount = selectedProduct?.variants?.length ?? 0;
                
                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    isSelected={!!selectedProduct}
                    selectedVariantsCount={selectedVariantsCount}
                    isExpanded={expanded[product.id] || false}
                    onToggleProduct={() => handleProductToggle(product)}
                    onToggleExpand={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [product.id]: !prev[product.id],
                      }))
                    }
                    onToggleVariant={(variant) => handleVariantToggle(product, variant)}
                    isVariantSelected={(variantId) =>
                      selectedProduct?.variants.some((v) => v.id === variantId) || false
                    }
                  />
                );
              })}
            </div>
          )
        ) : selectedVariants.length > 0 ? (
          <div className="space-y-3">
            {selectedVariants.map((selectedProduct) => {
              const fullProduct = rawData.find((p) => p.id === selectedProduct.id);
              const productImage = selectedProduct.images?.[0] 
                ? generateImageSrc(selectedProduct.images[0])
                : null;
              
              if (!fullProduct) return null;
              
              return (
                <div key={selectedProduct.id} className="bg-background border border-border rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between p-4 bg-secondary-background/30">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted border border-border/50">
                        {productImage ? (
                          <img
                            src={productImage}
                            alt={selectedProduct.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-[15px]">{selectedProduct.title}</h3>
                        <p className="text-[13px] text-muted-foreground">
                          {selectedProduct.variants.length} of {selectedProduct.totalVariants} variants selected
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        setSelectedVariants((prev) =>
                          prev.filter((p) => p.id !== selectedProduct.id),
                        )
                      }
                      className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Selected Variants */}
                  <div className="p-4 space-y-2">
                    {selectedProduct.variants.map((variant) => {
                      const variantImage = variant.image 
                        ? generateImageSrc(variant.image)
                        : null;
                      const formattedPrice = variant.amount 
                        ? `₹${(variant.amount / 100).toFixed(2)}`
                        : null;
                        
                      return (
                        <div
                          key={variant.id}
                          className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {variantImage && (
                              <div className="w-10 h-10 rounded overflow-hidden bg-muted border border-border/50 shrink-0">
                                <img
                                  src={variantImage}
                                  alt={variant.title || "Variant"}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-[14px] font-medium truncate">{variant.title && variant.title.trim() !== "" ? variant.title : "Default Variant"}</p>
                              <div className="flex items-center gap-3 mt-0.5">
                                {variant.sku && (
                                  <p className="text-[12px] text-muted-foreground font-mono">{variant.sku}</p>
                                )}
                                {formattedPrice && (
                                  <p className="text-[12px] font-semibold text-primary">{formattedPrice}</p>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedVariants((prev) => {
                                const updated = prev
                                  .map((p) => {
                                    if (p.id !== selectedProduct.id) return p;
                                    const newVariants = p.variants.filter((v) => v.id !== variant.id);
                                    if (newVariants.length === 0) return undefined;
                                    return { ...p, variants: newVariants };
                                  })
                                  .filter((p): p is SelectedProduct => !!p);
                                return updated;
                              });
                            }}
                            className="h-8 w-8 flex items-center justify-center rounded hover:bg-destructive/10 text-destructive transition-colors shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            No products selected.
          </div>
        )}
      </div>
      {/* Sticky Pagination + Save/Cancel Bar */}
      <div className="flex items-center justify-between gap-2 sticky bottom-0 bg-background py-4 border-t px-6 rounded-b-xl z-10">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted">
            {currentActiveTab === "selected"
              ? selectedVariants.length === 0
                ? "No products selected"
                : `Showing ${selectedVariants.length} selected product${selectedVariants.length !== 1 ? "s" : ""}`
              : `Showing ${(pageIndex - 1) * pageSize + 1} to ${Math.min(pageIndex * pageSize, total || 0)} of ${total || 0} entries`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 rounded border border-border hover:bg-muted/30 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={pageIndex === 1 || currentActiveTab === "selected"}
            onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
          >
            ‹
          </button>
          {/* Sliding window pagination logic with ellipsis */}
          {currentActiveTab === "all" &&
            (() => {
              const pages = [];
              const maxVisible = 5;
              if (totalPages <= maxVisible) {
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`px-3 py-1 rounded border ${pageIndex === i ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/30"}`}
                      onClick={() => setPageIndex(i)}
                    >
                      {i}
                    </button>,
                  );
                }
              } else {
                let start = Math.max(1, pageIndex - 2);
                let end = Math.min(totalPages, pageIndex + 2);
                if (start === 1) {
                  end = maxVisible;
                } else if (end === totalPages) {
                  start = totalPages - maxVisible + 1;
                }
                if (start > 1) {
                  pages.push(
                    <button
                      key={1}
                      className={`px-3 py-1 rounded border ${pageIndex === 1 ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/30"}`}
                      onClick={() => setPageIndex(1)}
                    >
                      1
                    </button>,
                  );
                  if (start > 2) {
                    pages.push(
                      <span key="start-ellipsis" className="px-2 text-muted">
                        ...
                      </span>,
                    );
                  }
                }
                for (let i = start; i <= end; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`px-3 py-1 rounded border ${pageIndex === i ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/30"}`}
                      onClick={() => setPageIndex(i)}
                    >
                      {i}
                    </button>,
                  );
                }
                if (end < totalPages) {
                  if (end < totalPages - 1) {
                    pages.push(
                      <span key="end-ellipsis" className="px-2 text-muted">
                        ...
                      </span>,
                    );
                  }
                  pages.push(
                    <button
                      key={totalPages}
                      className={`px-3 py-1 rounded border ${pageIndex === totalPages ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/30"}`}
                      onClick={() => setPageIndex(totalPages)}
                    >
                      {totalPages}
                    </button>,
                  );
                }
              }
              return pages;
            })()}
          <button
            className="px-3 py-1 rounded border border-border hover:bg-muted/30 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={
              pageIndex === totalPages || currentActiveTab === "selected"
            }
            onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
